import React from 'react';
import { get, partial } from 'lodash';

import { isBrowser } from '@zapier/common-utils';
import { CrossBrowserDocument, TrackPropsType } from './types';

// this component will track the amount of time its child components were being
// actively viewed by the user.
// Before the window is closed it will send an event with a cumulative amount of
// active and absent time, as well as any tracking props that are passed to it.

/*

To use, just wrap whatever component for which you want to track time.
trackingProps and trackingMessage are optional, but a tracking function is required.

For example:

<TrackTime
    trackingProps={{root_id: 1234}}
    trackingMessage="track time my great component"
    track={this.props.actions.track.track}
>
    <MyGreatComponent />
</TrackTime>

When this component unmounts from the page, or if the user otherwise
refreshes or navigates away, an event will be thrown with the
tracking message and its props. The props `totalActiveMillis` and `totalAbsentMillis`
are the cumulative amount of time the user spent with the component in active view
or not in view, i.e. in another tab or scrolled past the component. If the user's
mouse leaves the tab or window, or if they tab away, that will count as absent time.
This isn't a foolproof method though -- if the window or tab is force-closed, the event won't fire.

Otherwise, the event fires when the users closes the tab/window or navigates away from
the component. It attaches two props to the tracking props, `totalACtiveMillis` and `totalAbsentMillis`, which are the active/absent time calculated in milliseconds.

If you aren't sure how the time will be counted, it's best to
add a state logging function as the second argument of the setState calls in `onChangeUserStatus` so that you can watch when the visibility changes and how that affects the time counted.

*/

type LastEvent = 'userActive' | 'userInactive';

type StateType = {
  hidden: string;
  visibilityChange: string;
  totalActiveMillis: number;
  totalAbsentMillis: number;
  lastEventTime: number;
  lastEvent: LastEvent;
  userState: 'active' | 'inactive';
};

const getVisibilityChangeProps = () => {
  if (typeof document.hidden !== 'undefined') {
    // This covers most cases
    return {
      hidden: 'hidden',
      visibilityChange: 'visibilitychange',
    };
  }
  // @ts-ignore
  else if (typeof (document as CrossBrowserDocument).msHidden !== 'undefined') {
    // Internet Explorer and Microsoft Edge
    return {
      hidden: 'msHidden',
      visibilityChange: 'msvisibilitychange',
    };
  } else if (
    typeof (document as CrossBrowserDocument).webkitHidden !== 'undefined'
  ) {
    // Older versions of webkit browsers (Chrome, Safari, newer versions of Opera, almost all iOS browsers (including Firefox for iOS))
    return {
      hidden: 'webkitHidden',
      visibilityChange: 'webkitvisibilitychange',
    };
  }
  return undefined;
};

class TrackTime extends React.Component<TrackPropsType, StateType> {
  public state: StateType = {
    hidden: '',
    visibilityChange: '',
    totalActiveMillis: 0,
    totalAbsentMillis: 0,
    lastEventTime: Date.now(),
    // sets the user state active,
    // which requires a previous event of userInactive to kick things off.
    lastEvent: 'userInactive',
    userState: 'active',
  };

  componentWillUnmount() {
    if (isBrowser()) {
      this.onClosePage();
    }
  }

  componentDidMount() {
    if (isBrowser()) {
      const visibilityChangeProps = getVisibilityChangeProps();
      if (visibilityChangeProps) {
        this.setState(visibilityChangeProps, this.beginEventListening);
      }
    }
  }

  getTimeDelta: () => { now: number; elapsed: number } = () => {
    const now = Date.now();
    return {
      now,
      elapsed: now - this.state.lastEventTime,
    };
  };

  onChangeUserStatus = (statusType: LastEvent) => {
    const { totalAbsentMillis, totalActiveMillis, userState } = this.state;

    const { elapsed, now } = this.getTimeDelta();
    // we're switching from 'inactive' to 'active', so the event will be 'userActive'
    // and the current userState will be 'inactive'
    // since we have a few events handling a visibility change (mouseleave, mousenter, and the
    // events triggered by the visibility change api) we need to double-check that we're not already // counting active/inactive time before we change the user status.
    if (statusType === 'userActive' && userState === 'inactive') {
      this.setState({
        totalAbsentMillis: totalAbsentMillis + elapsed,
        lastEvent: 'userActive',
        lastEventTime: now,
        userState: 'active',
      });
    } else if (statusType === 'userInactive' && userState === 'active') {
      this.setState({
        totalActiveMillis: totalActiveMillis + elapsed,
        lastEvent: 'userInactive',
        lastEventTime: now,
        userState: 'inactive',
      });
    }
  };

  onUserActive: () => void = partial(this.onChangeUserStatus, 'userActive');

  onUserInactive: () => void = partial(this.onChangeUserStatus, 'userInactive');

  beginEventListening = () => {
    document.addEventListener(
      this.state.visibilityChange,
      this.handleVisibilityChange
    );
    window.addEventListener('beforeunload', this.onClosePage);
    document.addEventListener('mouseleave', this.onUserInactive);
    document.addEventListener('mouseenter', this.onUserActive);
    this.onUserActive();
  };

  onClosePage = () => {
    const { totalActiveMillis, totalAbsentMillis, userState } = this.state;
    const trackingProps = this.props.trackingProps || {};
    const { elapsed } = this.getTimeDelta();
    const message = this.props.trackingMessage || 'track time';
    this.props.track(message, {
      ...trackingProps,
      totalActiveTime:
        totalActiveMillis + (userState === 'active' ? elapsed : 0),
      totalAbsentTime:
        totalAbsentMillis + (userState === 'inactive' ? elapsed : 0),
    });
    document.removeEventListener('mouseleave', this.onUserInactive);
    window.removeEventListener('beforeunload', this.onClosePage);
    document.removeEventListener('mouseenter', this.onUserActive);
  };

  handleVisibilityChange = () => {
    if (get(document, [this.state.hidden])) {
      this.onUserInactive();
    } else {
      this.onUserActive();
    }
  };

  render() {
    return this.props.children;
  }
}

export default TrackTime;
